import { Suspense, useEffect } from 'react';

import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { tracking } from '@iwoca/frontend-tracking-library';
import { H1 } from '@ui';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { DocumentFeedback } from './documentFeedback/DocumentFeedback';
import LinkedAccounts from './linkedAccounts/LinkedAccounts';
import styles from './TradingData.module.css';
import { UploadedDocuments } from './uploadedDocuments/UploadedDocuments';
import { useGetTradingHistoryData } from './useGetTradingHistoryData';
import { ErrorBoundary } from '../../errorBoundary/ErrorBoundary';
import {
    getQueryParameter,
    getWindowLocation,
    isLinkingOpenBankingOnly
} from '../../iwRouter/LocationHelper';
import { UploadSVG } from '../../svgComponents/uploadSVG';
import { useTrackPageView } from '../../tracking/pageView';
import FallbackErrorModal from '../modals/fallbackErrorModal/FallbackErrorModal';
import { OPEN_BANKING_MODAL } from '../modals/linkOpenBankingModal/LinkOpenBankingModal.actions';
import { SHOW_UPLOAD_DOCUMENTS_MODAL } from '../modals/uploadDocumentsModal/UploadDocumentsModal.actions';
import MainContentPending from '../pageStructure/mainContentPending';
import { MainSectionWrapper } from '../pageStructure/mainSectionWrapper';

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        uploadNewDocumentType: () =>
            dispatch({ type: SHOW_UPLOAD_DOCUMENTS_MODAL }),
        openLinkBankAccountModal: () => dispatch({ type: OPEN_BANKING_MODAL })
    };
};

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

const TradingData = ({ ...props }: PropsFromRedux) => {
    useTrackPageView({ name: 'Trading Data' });

    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <div
                        className="col-span-2"
                        data-testid="trading-data-loading"
                    >
                        <MainContentPending />
                    </div>
                }
            >
                <div className="col-span-2 row-span-2 max-sm:order-last max-sm:col-span-full">
                    <TradingDataContent {...props} />
                </div>
            </Suspense>
        </ErrorBoundary>
    );
};

const TradingDataContent = ({
    uploadNewDocumentType,
    openLinkBankAccountModal
}: PropsFromRedux) => {
    const { uploadedDocuments, hasDocumentFeedback } =
        useGetTradingHistoryData();

    useEffect(() => {
        tracking.ampli.tradingPageLoaded();
    }, []);

    const location = getWindowLocation();
    const linkOpenBankingModalQueryParam =
        getQueryParameter(location, 'modal') === 'LinkOpenBankingModal';
    const truelayerRefererQueryParam =
        getQueryParameter(location, 'referer') === 'truelayer_callback';

    if (linkOpenBankingModalQueryParam && truelayerRefererQueryParam)
        openLinkBankAccountModal();

    return (
        <div data-testid="tradingData" className={styles.TradingData}>
            <H1 className="mx-auto max-w-[60rem] py-xl">
                <FormattedMessage
                    id="_.trading_data.title"
                    defaultMessage="Upload Documents"
                />
            </H1>

            <MainSectionWrapper>
                <DEOnly>{hasDocumentFeedback && <DocumentFeedback />}</DEOnly>
                <LinkedAccounts />
                {!isLinkingOpenBankingOnly() && (
                    <>
                        <UploadedDocuments
                            uploadedDocuments={uploadedDocuments}
                        />
                        <button
                            className={styles.uploadNewDocumentType}
                            onClick={uploadNewDocumentType}
                            title="upload document button"
                        >
                            <UploadSVG className={styles.uploadIcon} />
                            <FormattedMessage
                                id="_.trading_data.upload_new_document_type"
                                defaultMessage="Upload new document type"
                            />
                        </button>
                    </>
                )}
            </MainSectionWrapper>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(TradingData);
